import React, { createContext, useContext, useState, useEffect } from 'react';
import { CategoryInsightsSettingsContext } from 'contexts/CategoryInsightsSettingsContext';
import { CategoryManagementContext } from 'contexts/CategoryManagementContext';
import { getSpendComparisonByYear } from 'utils/apiRequests';
import { FONTSTYLE } from 'CategoryManagement/Dashboard/ChartsList/chartUtils';
import { colors } from '@alixpartners/ui-components';
import { renderToString } from 'react-dom/server';
import {
	TooltipColor,
	TooltipContainer,
	TooltipItem,
	TooltipItemName,
	TooltipItemTitle,
	TooltipItemValue,
} from './DashboardChart.styles';
import { formatSpendWithDecimalAndSymbol } from 'utils/number';

const pageSize = 15;

const SpendByYear = createContext();

export const SpendByYearProvider = ({
	children,
	currencySymbol,
	hideDecimals,
}) => {
	const [isLoading, setIsLoading] = useState(true);
	const [data, setData] = useState([]);
	const [currentPage, setCurrentPage] = useState(1);
	const [options, setOptions] = useState({
		chart: { type: 'column' },
		title: { text: '' },
		exporting: { enabled: false, allowHTML: true },
		credits: { enabled: false },
		loading: {
			hideDuration: 500,
			showDuration: 500,
			labelStyle: {
				fontWeight: 'normal',
				letterSpacing: '0.3px',
			},
		},
		xAxis: {
			categories: [],
			title: {
				text: ``,
				style: { ...FONTSTYLE },
			},
			scrollbar: {
				enabled: true,
			},
			lineColor: colors.black,
			gridLineWidth: 0,
			labels: {
				style: {
					fontSize: '9px',
				},
			},
			// labels: {
			// 	formatter: function() {
			// 		if (typeof this.value !== 'number') {
			// 			if (this.value === undefined) {
			// 				return '';
			// 			} else {
			// 				if (this.value.length > 0) {
			// 					return `${this.value.substring(0, 4)}...`;
			// 				}
			// 			}
			// 		}
			// 	},
			// 	style: { ...FONTSTYLE },
			// },
		},
		yAxis: {
			gridLineWidth: 0,
			lineColor: colors.black,
			lineWidth: 1,
			labels: {
				formatter: function() {
					return formatSpendWithDecimalAndSymbol(
						this.value,
						currencySymbol,
						hideDecimals,
						0
					).toUpperCase();
				},
				style: {
					...FONTSTYLE,
				},
			},
			title: {
				text: 'Spend',
				style: {
					...FONTSTYLE,
					textOverflow: 'ellipsis',
				},
			},
		},
		legend: {
			enabled: true,
		},
		tooltip: {
			shared: true,
			useHTML: true,
			backgroundColor: colors.white,
			borderWidth: 0,
			outside: true,
			formatter: function() {
				return renderToString(
					<TooltipContainer>
						<TooltipItemTitle>{this.x}</TooltipItemTitle>
						{this.points.map(item => (
							<TooltipItem key={`${item.point.series.name}-${item.point.y}`}>
								<span>
									<TooltipColor color={item.color} />
									<TooltipItemName>
										{item.point.series.name?.split('(')[0]}
									</TooltipItemName>
								</span>
								<TooltipItemValue>
									{formatSpendWithDecimalAndSymbol(
										item.point.y,
										currencySymbol,
										hideDecimals,
										2
									).toLocaleUpperCase()}
								</TooltipItemValue>
							</TooltipItem>
						))}
					</TooltipContainer>
				);
			},
			borderRadius: 2,
		},
		plotOptions: {
			column: {
				pointPadding: 0.2,
				borderWidth: 0,
			},
		},
		series: [],
		colors: ['#032E45', '#448EB4', '#45B9F7', '#AFE1FC'],
	});

	const {
		currentSelectCategory,
		dateRangeDataSpendByYear,
		isFetching,
		isDirect,
		isSourceable,
		taxonomyType,
	} = useContext(CategoryManagementContext);
	const { loading: isSettingsFetching } = useContext(
		CategoryInsightsSettingsContext
	);
	const { categoryName } = currentSelectCategory;
	const { dateFrom, dateTo } = dateRangeDataSpendByYear;

	function calculateMonthsBetween(dateFrom, dateTo) {
		const startDate = new Date(dateFrom);
		const endDate = new Date(dateTo);

		const yearsDifference = endDate.getFullYear() - startDate.getFullYear();
		const monthsDifference = endDate.getMonth() - startDate.getMonth();

		let totalMonths = yearsDifference * 12 + monthsDifference;

		if (endDate.getDate() >= startDate.getDate() || monthsDifference >= 0) {
			totalMonths++;
		}

		return totalMonths;
	}

	function subtractMonths(dateStr, monthsToSubtract) {
		const date = new Date(dateStr);
		date.setMonth(date.getMonth() - monthsToSubtract);

		return date;
	}

	const formatter = new Intl.DateTimeFormat('en-US', {
		year: 'numeric',
		month: 'short',
	});

	useEffect(
		() => {
			if (!isSettingsFetching && !isFetching) {
				setIsLoading(true);
				if (
					categoryName !== '' ||
					categoryName !== null ||
					categoryName !== undefined
				) {
					const payload = {
						categoryName: categoryName,
						dateFrom: dateFrom,
						dateTo: dateTo,
						isSourceable: isSourceable,
						aptaxonomy: isDirect,
						taxonomyType: taxonomyType,
					};
					getSpendComparisonByYear(payload)
						.then(response => {
							if (response.succeeded) {
								if (response.data !== null && response.data.length > 0) {
									const sortedData = response.data.sort(
										(a, b) => b.currentYearSpend - a.currentYearSpend
									);
									setData(sortedData);
									setIsLoading(false);
								} else {
									setIsLoading(false);
								}
							}
						})
						.catch(() => setIsLoading(false));
				}
			}
		}, // eslint-disable-next-line
		[
			dateRangeDataSpendByYear,
			categoryName,
			isSettingsFetching,
			isFetching,
			isDirect,
			isSourceable,
			taxonomyType,
		]
	);

	useEffect(
		() => {
			if (data.length > 0) {
				const firstPageIndex = (currentPage - 1) * pageSize;
				const lastPageIndex = firstPageIndex + pageSize;
				const batchRecords = data.slice(firstPageIndex, lastPageIndex);

				const categoriesData = batchRecords.map(x => x.vendoR_NAME);
				const months = calculateMonthsBetween(dateFrom, dateTo);
				let prevFrom = subtractMonths(dateFrom, months);
				let prevTo = subtractMonths(dateFrom, 1);

				const seriesData = [
					{
						name: `Current Year Spend ( ${formatter.format(
							new Date(dateFrom)
						)} - ${formatter.format(new Date(dateTo))} )`,
						data: batchRecords.map(x => x.currentYearSpend),
						additionalInfo: 'currentYearSpend',
					},
					{
						name: `Previous Year Spend ( ${formatter.format(
							prevFrom
						)} - ${formatter.format(prevTo)} )`,
						data: batchRecords.map(x => x.previousYearSpend),
						additionalInfo: 'previousYearSpend',
					},
				];
				setOptions(prevState => ({
					...prevState,
					xAxis: {
						...prevState.xAxis,
						categories: categoriesData,
					},
					series: seriesData,
				}));
			}
		}, // eslint-disable-next-line
		[data, currentPage]
	);

	return (
		<SpendByYear.Provider
			value={{
				data,
				isLoading,
				options,
				currentPage,
				setCurrentPage,
				pageSize,
			}}
		>
			{children}
		</SpendByYear.Provider>
	);
};

export const useSpendByYearContext = () => {
	return useContext(SpendByYear);
};
